const sheet = new CSSStyleSheet();
sheet.replaceSync(`
  :host {
    position: fixed;
    z-index: -1000;
    opacity: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: scroll;
    overflow-x: hidden;
    background: rgba(0, 0, 0, 0.7);
    visibility: hidden;
    transition: z-index 0.2s step-end, opacity 0.2s linear, visibility 0.3s ease-in-out;
  }
  :host([type="top"]) {
    left: 0;
    justify-content: flex-end;
    align-items: flex-end;
  }
  :host([type="bottom"]) {
    align-items: flex-start;
  }
  :host([type="right"]) {
    right: 0;
    justify-content: flex-end;
    align-items: stretch;
  }
  :host([type="left"]) {
    left: 0;
    justify-content: flex-start;
    align-items: stretch;
  }
  :host([open]) {
    display: flex;
    z-index: 100;
    opacity: 1;
    visibility: visible;
    transition: z-index 0.4s step-start, opacity 0.2s linear, visibility 0.2s ease-in-out;
  }
  :host > ::slotted(div) {
    position: relative;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    background: #fff;
    border-top-width: 1px;
    border-color: #c4c4c4;
    transition: transform 0.4s;
  }
  :host([type="top"]) > ::slotted(div) {
    transform: translateY(200%);
    box-shadow: -10px -10px 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="bottom"]) > ::slotted(div) {
    transform: translateY(-200%);
    box-shadow: 0 10px 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="top"][open]) > ::slotted(div),
  :host([type="bottom"][open]) > ::slotted(div) {
    transform: translateY(0);
  }
  :host([type="right"]) > ::slotted(div) {
    transform: translateX(200%);
    box-shadow: -10px 0 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="left"]) > ::slotted(div) {
    transform: translateX(-200%);
    box-shadow: 10px 0 5px 2px rgb(0 0 0 / 10%);
  }
  :host([type="right"][open]) > ::slotted(div),
  :host([type="left"][open]) > ::slotted(div) {
    transform: translateX(0);
  }
`);

class LabModal extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({mode: 'open'});
    this.shadowRoot.adoptedStyleSheets = [sheet];
    this.shadowRoot.innerHTML = `<slot></slot>`;

    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true');
    }

    this.isBodyScrollLockDisabled = this.hasAttribute('disable-body-lock');
    this.modalId = this.id;
  }

  connectedCallback() {
    this.registerModal();

    this.addEventListener('click', this.handleModalClick);
    document.addEventListener('keydown', this.handleEscKey.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleModalClick);
    document.removeEventListener('keydown', this.handleEscKey.bind(this));
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'open') return;

    if (oldValue === null) this.openModal();
    else if (newValue === null) this.closeModal();
  }

  registerModal() {
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);

    window.theme.modals = window.theme.modals || [];

    window.theme.modals[this.modalId] = {
      open: this.openModal,
      close: this.closeModal,
    };
  }

  openModal() {
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');

    if (!this.isBodyScrollLockDisabled) {
      LabModal.lockBodyScroll();
    }

    this.focus();

    document.dispatchEvent(
      new CustomEvent(`modal-opened-${this.modalId}`, {
        bubbles: true,
        detail: {
          modalId: this.modalId,
        },
      })
    );
  }

  closeModal() {
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');

    if (!this.isBodyScrollLockDisabled) {
      LabModal.unlockBodyScroll();
    }

    document.dispatchEvent(
      new CustomEvent(`modal-closed-${this.modalId}`, {
        bubbles: true,
        detail: {
          modalId: this.modalId,
        },
      })
    );
  }

  handleModalClick(event) {
    event.stopPropagation();

    if (event.target !== this) return;

    this.closeModal();
  }

  handleEscKey(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      this.closeModal();
    }
  }

  static lockBodyScroll() {
    document.body.classList.add('lock-scroll');
  }

  static unlockBodyScroll() {
    document.body.classList.remove('lock-scroll');
  }
}

customElements.define('lab-modal', LabModal);
